<template>
    <div>
        <div class="d-flex flex-column flex-lg-row justify-content-lg-between mb-5">
            <div>
                <h1 class="mb-4">
                    My Calendar
                </h1>
                <p class="text-demibold">
                    Here you can manage all your property appointments. Cancelling or Accepting appointments is as easy as selecting them from the below columns and following any instructions. To edit specific property availability, simply click the link and select the property to edit.
                </p>
            </div>

            <div>
                <router-link
                    :to="{ name: 'my-properties' }"
                    class="btn btn--primary"
                >
                    Property Availability
                </router-link>
            </div>
        </div>

        <!-- TODO: Add 'to be confirmed events' -->
        <!-- TODO: Add function for accepting 'to be confirmed' events, that adds them to the 'events array' -->
        <!-- TODO: Add function for rejecting 'to be confirmed' events, with a 'reason' textarea -->

        <b-row>
            <b-col
                cols="12"
                xl="6"
            >
                <div class="card card--appointments">
                    <div class="card__body d-flex flex-column">
                        <h2 class="card__title color-dark-grey mb-3">
                            Unconfirmed
                        </h2>
                        <p class="mb-5">
                            This is where your unconfirmed appointments will show. To accept or a cancel an appointment request simply click the corresponding icon
                        </p>

                        <!-- Table listing -->
                        <div
                            v-if="unconfirmedEvents"
                            class="table-flex table-messages table-flex--plain table-flex--cell-4-md"
                        >
                            <div class="table-flex--header">
                                <div>
                                    Label
                                </div>
                                <div>
                                    Person
                                </div>
                                <div>
                                    Date
                                </div>
                                <div />
                            </div>
                            <div
                                v-for="event in unconfirmedEvents"
                                :key="event.id"
                            >
                                <div>
                                    <p class="p-sm mb-0 color-primary">
                                        {{ event.label }}
                                    </p>
                                </div>
                                <div>
                                    <p class="p-sm mb-0">
                                        {{ event.user.full_name }}
                                    </p>
                                </div>
                                <div>
                                    <p class="p-sm mb-0">
                                        {{ event.start_at | moment('L h:mm a') }}
                                    </p>
                                </div>
                                <div class="justify-content-end">
                                    <button
                                        class="btn btn--nomin btn--sm btn--outline btn--icon"
                                        @click="eventConfirm(event.id)"
                                    >
                                        <SVGSprite name="checked-thin" />
                                    </button>
                                    <button
                                        class="btn btn--nomin btn--sm btn--outline btn--icon"
                                        @click="(currentEvent = event) && $refs.modalDeclineEvent.show()"
                                    >
                                        <SVGSprite name="close" />
                                    </button>
                                    <button
                                        class="btn btn--nomin btn--sm btn--outline btn--icon"
                                        @click="eventClicked(event.id)"
                                    >
                                        <SVGSprite name="eye" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-col>
            <b-col
                cols="12"
                xl="6"
            >
                <div class="card card--appointments">
                    <div class="card__body d-flex flex-column">
                        <h2 class="card__title color-dark-grey mb-3">
                            Confirmed
                        </h2>
                        <p class="mb-5">
                            To cancel a confirmed appointment simply click the view icon and give a reason for your cancellation
                        </p>

                        <!-- Table listing -->
                        <div
                            v-if="confirmedEvents"
                            class="table-messages table-flex table-flex--plain table-flex--cell-4-md"
                        >
                            <div class="table-flex--header">
                                <div>
                                    Label
                                </div>
                                <div>
                                    Person
                                </div>
                                <div>
                                    Date
                                </div>
                                <div />
                            </div>
                            <div
                                v-for="event in confirmedEvents"
                                :key="event.id"
                            >
                                <div>
                                    <p class="p-sm mb-0 color-primary">
                                        {{ event.label }}
                                    </p>
                                </div>
                                <div>
                                    <p class="p-sm mb-0">
                                        {{ event.user.full_name }}
                                    </p>
                                </div>
                                <div>
                                    <p class="p-sm mb-0">
                                        {{ event.start_at | moment('DD/MM/YYYY') }}
                                    </p>
                                </div>
                                <div class="justify-content-end">
                                    <button
                                        class="btn btn--nomin btn--sm btn--outline btn--icon"
                                        @click="eventClicked(event.id, event)"
                                    >
                                        <SVGSprite name="eye" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- END Table listing -->
                    </div>
                </div>
            </b-col>
        </b-row>
        <!-- END Table listing -->

        <div id="my-calendar">
            <b-card
                class="p-4"
            >
                <header class="v-cal-header">
                    <div class="v-cal-header__actions">
                        <div class="actions-left">
                            <button
                                class="btn btn--secondary btn--nomin btn--sm"
                                :class="{ 'v-cal-button--is-active': calendarActiveDate === $moment().format('YYYY-MM-DD') }"
                                type="button"
                                @click="calendarActiveDate = $moment().format('YYYY-MM-DD')"
                            >
                                Today
                            </button>
                            <button
                                class="btn btn--secondary btn--nomin btn--sm"
                                type="button"
                                @click="$refs.calendar.prev()"
                            >
                                Back
                            </button>
                            <button
                                class="btn btn--secondary btn--nomin btn--sm"
                                type="button"
                                @click="$refs.calendar.next()"
                            >
                                Next
                            </button>
                        </div>
                        <div class="v-cal-header__title-bar">
                            <h3 class="v-cal-header__title">
                                {{ calendarActiveDate | moment('MMMM YYYY') }}
                            </h3>
                        </div>
                        <div class="actions-right">
                            <button
                                type="button"
                                :class="{ 'v-cal-button--is-active': calendarView === 'month' }"
                                class="btn btn--secondary btn--nomin btn--sm"
                                @click="calendarView = 'month'"
                            >
                                Month
                            </button>
                            <button
                                type="button"
                                :class="{ 'v-cal-button--is-active': calendarView === 'week' }"
                                class="btn btn--secondary btn--nomin btn--sm"
                                @click="calendarView = 'week'"
                            >
                                Week
                            </button>
                            <button
                                type="button"
                                :class="{ 'v-cal-button--is-active': calendarView === 'day' }"
                                class="btn btn--secondary btn--nomin btn--sm"
                                @click="calendarView = 'day'"
                            >
                                Day
                            </button>
                        </div>
                    </div>
                </header>
                <b-row class="justify-content-lg-center">
                    <b-col
                        cols="12"
                        lg="8"
                    >
                        <div class="field-wrap p-4 bg-light-grey rounded-corners">
                            <b-row class="align-items-center">
                                <b-col
                                    cols="12"
                                    lg="4"
                                >
                                    <h4 class="mb-4 mb-lg-0 color-mid-grey text-bold">
                                        Filter by my properties:
                                    </h4>
                                    <label
                                        for="field-property-filter"
                                        class="sr-only"
                                    >
                                        Filter by property:
                                    </label>
                                </b-col>
                                <b-col
                                    cols="12"
                                    lg="8"
                                >
                                    <div class="field-wrap__input field-wrap__input--select">
                                        <select
                                            id="field-property-filter"
                                            v-model="propertyFilter"
                                            class="py-3"
                                            name="field-property-filter"
                                        >
                                            <option
                                                :value="0"
                                                selected
                                            >
                                                All properties
                                            </option>
                                            <option
                                                v-for="property in properties"
                                                :key="property.id"
                                                :value="property.id"
                                            >
                                                {{ property.full_address }}
                                            </option>
                                        </select>
                                    </div>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
                <v-calendar
                    ref="calendar"
                    v-model="calendarActiveDate"
                    :type="calendarView"
                    :show-month-on-first="false"
                    first-interval="16"
                    interval-count="26"
                    interval-minutes="30"
                    @click:date="calendarView = 'day'"
                >
                    <template
                        v-slot:day="{ date }"
                    >
                        <template
                            v-if="calendarEvents[date]"
                        >
                            <button
                                v-for="event in calendarEvents[date]"
                                :key="event.id"
                                class="btn-event-day bg-secondary mb-3 p-3 w-100 rounded-corners text-left"
                                @click="eventClicked(event.id)"
                            >
                                <h3 class="h4 color-white event-label">
                                    {{ event.label }}
                                </h3>
                                <p class="event-time">
                                    {{ event.start_at | moment('h:mma') }} - {{ event.end_at | moment('h:mma') }}
                                </p>
                                <SVGSprite
                                    name="calendar"
                                    class="event-icon color-white"
                                />
                            </button>
                        </template>
                    </template>

                    <template v-slot:interval="{ date, time }">
                        <template
                            v-if="calendarEvents[date]"
                        >
                            <button
                                v-for="event in calendarEvents[date].filter(item => time === $moment(item.start_at).format('kk:mm'))"
                                :key="event.id"
                                class="btn-event bg-secondary mb-3 p-3 w-100 text-left"
                                @click="eventClicked(event.id)"
                            >
                                <h3 class="h4 color-white">
                                    {{ event.label }}
                                </h3>
                                <p>{{ event.start_at | moment('h:mma') }} - {{ event.end_at | moment('h:mma') }}</p>
                            </button>
                        </template>
                    </template>
                </v-calendar>
            </b-card>
        </div>

        <b-modal
            id="modal-decline-event"
            ref="modalDeclineEvent"
            title=""
            ok-title="Decline Event"
            cancel-title="Close"
            @ok="eventDecline"
        >
            <p>Are you sure you want to decline this event? If so, please provide a reason below.</p>
            <label
                for="decline-reason"
                class="sr-only"
            >
                Decline Reason
            </label>
            <div class="field-wrap__input">
                <textarea
                    id="decline-reason"
                    v-model="declineReason"
                    type="textarea"
                    name="cancel-reason"
                    placeholder="Please add the reason for the declining of the event"
                    rows="5"
                />
            </div>
        </b-modal>

        <b-modal
            id="modal-view-event"
            ref="modalViewEvent"
            title=""
            size="md"
            hide-footer
        >
            <div
                v-if="currentEvent"
                class="event-info"
            >
                <div v-if="currentEvent.type">
                    <h5 class="text-bold">
                        Type
                    </h5>
                    <p class="mb-0">
                        {{ currentEvent.label }}
                    </p>
                </div>
                <div v-if="currentEvent.start_at">
                    <h5 class="text-bold">
                        Date
                    </h5>
                    <p class="mb-0">
                        {{ currentEvent.start_at | moment('DD/MM/YYYY') }}
                    </p>
                </div>
                <div v-if="currentEvent.start_at">
                    <h5 class="text-bold">
                        Start Time
                    </h5>
                    <p class="mb-0">
                        {{ currentEvent.start_at | moment('h:mma') }}
                    </p>
                </div>
                <div v-if="currentEvent.end_at">
                    <h5 class="text-bold">
                        End Time
                    </h5>
                    <p class="mb-0">
                        {{ currentEvent.end_at | moment('h:mma') }}
                    </p>
                </div>
                <template v-if="currentEvent.user">
                    <div v-if="currentEvent.user.full_name">
                        <h5 class="text-bold">
                            Requester Name
                        </h5>
                        <p class="mb-0">
                            {{ currentEvent.user.full_name }}
                        </p>
                    </div>
                    <div v-if="currentEvent.user.telephone || currentEvent.user.mobile_telephone">
                        <h5 class="text-bold">
                            Requester Phone
                        </h5>
                        <p class="mb-0">
                            {{ currentEvent.user.telephone || currentEvent.user.telephone }}
                        </p>
                    </div>
                    <div v-if="currentEvent.user.email">
                        <h5 class="text-bold">
                            Requester Email
                        </h5>
                        <p class="mb-0">
                            <a :href="`mailto:${currentEvent.user.email}`">
                                {{ currentEvent.user.email }}
                            </a>
                        </p>
                    </div>
                </template>
                <div v-if="currentEvent.property_name">
                    <h5 class="text-bold">
                        Requester Address
                    </h5>
                    <p class="mb-0">
                        {{ currentEvent.property_name }}
                    </p>
                </div>
            </div>
            <div
                v-if="currentEvent"
                class="mt-5"
            >
                <b-collapse
                    id="cancel-toggle"
                    @show="cancelButtonText = 'Undo Cancel'"
                    @hide="cancelButtonText = 'Cancel Event'"
                >
                    <b-card>
                        <div class="field-wrap">
                            <label
                                for="cancel-reason"
                                class="sr-only"
                            >
                                Cancellation Reason
                            </label>
                            <div class="field-wrap__input">
                                <textarea
                                    id="cancel-reason"
                                    v-model="cancelReason"
                                    type="textarea"
                                    name="cancel-reason"
                                    placeholder="Please add reason for cancellation to be sent to the user"
                                    rows="5"
                                />
                            </div>
                        </div>
                    </b-card>
                </b-collapse>
                <div class="d-flex justify-content-between mt-5">
                    <div>
                        <b-btn
                            v-b-toggle.cancel-toggle
                            class="btn"
                        >
                            {{ cancelButtonText }}
                        </b-btn>
                    </div>
                    <div>
                        <transition name="fade">
                            <button
                                v-if="cancelReason !== ''"
                                class="btn btn--primary"
                                @click="eventCancel"
                            >
                                Cancel
                            </button>
                        </transition>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { VCalendar } from 'vuetify/lib/components/VCalendar';
import { groupBy } from 'lodash-es';
// NOTE: Customisations to confirm / investigate:
// customise event BG colour depending on data passed (func: event-created)
// edit existing information on events (func: event-clicked). It seems strange this functionality isn't already in place, I may have missed something (ie it pops the dialog box again?)
// have select boxes in the (event-dialog-config). This would be used for (event-type) and (address) potentially
// cant seem to pass HTML to the (eventDisplay) method. This means bolding certain parts of the label etc is not possible

export default {
    name: 'MyCalendar',
    components: {
        'v-calendar': VCalendar
    },
    data() {
        return {
            cancelConfirm: false,
            cancelButtonText: 'Cancel Event',
            currentEvent: {},
            declineReason: '',
            cancelReason: '',
            calendarActiveDate: this.$moment().format('YYYY-MM-DD'),
            calendarView: 'month',
            propertyFilter: 0
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapGetters('user', ['events', 'sortedEvents']),
        unconfirmedEvents() {
            try {
                return this.sortedEvents.requested;
            }
            catch (e) {
                return [];
            }
        },
        confirmedEvents() {
            try {
                return this.sortedEvents.approved;
            }
            catch (e) {
                return [];
            }
        },
        calendarEvents() {
            const events = this.propertyFilter ? this.events.filter(event => event.property_id === this.propertyFilter) : this.events;
            return groupBy(events, event => this.$moment(event.start_at).format('YYYY-MM-DD'));
        },
        properties() {
            return this.user.properties.filter(property => property.full_address);
        }
    },
    methods: {
        ...mapActions('user', ['confirmEvent', 'declineEvent', 'cancelEvent']),
        eventDisplay(event) {
            // data to be shown on the event items in the calendar
            return `${event.label}`;
        },
        eventCreated(event) {
            // TODO: On event creation add a custom ID to the data
            // TODO: On event creation post this to the laravel DB
        },
        eventClicked(id) {
            // get details of event clicked
            this.currentEvent = this.events.find(event => event.id === id);
            // show event information modal
            this.$refs.modalViewEvent.show();
        },
        eventConfirm(id) {
            // set current event to one clicked
            this.currentEvent = this.events.find(event => event.id === id);
            // confirm event
            this.confirmEvent({ id });
            // hide modal if open
            this.$refs.modalViewEvent.hide();
        },
        eventDecline() {
            // cancel event
            this.declineEvent({
                id: this.currentEvent.id,
                reason: this.declineReason
            });
            // hide modal if open
            this.$refs.modalViewEvent.hide();
            // set current event to null
            this.currentEvent = null;
            this.declineReason = '';
        },
        eventCancel() {
            // cancel event
            this.cancelEvent({
                id: this.currentEvent.id,
                reason: this.cancelReason
            });
            // hide modal if open
            this.$refs.modalViewEvent.hide();
            // set current event to null
            this.currentEvent = null;
            this.cancelReason = '';
            // TODO: on event deletion, use the 'reason' value in the currentEvent for email notifications
            // TODO: On event deletion, remove post from laravel DB
        }
    }
};
</script>

<style lang="scss">
.v-calendar {
    height: auto;

    .event-label,
    .event-time {
        display: none;

        @include media-breakpoint-up(md) {
            display: block;
        }
    }

    .event-icon {
        display: block;
        width: 32px;
        height: auto;
        max-width: 100%;
        margin: auto;

        @include media-breakpoint-up(md) {
            display: none;
        }
    }
}

.modal {
    &#modal-view-event {
        .modal-dialog {
            .modal-header {
                padding: 0;
                z-index: 2;
                background: transparent;

                .close {
                    color: theme-color('mid-grey');
                }
            }

            .modal-content {
                background: theme-color('light-grey');
            }
        }

        #cancel-toggle {
            .card {
                background: transparent;

                .card-body {
                    padding: 0;
                }
            }

            .field-wrap {
                margin-bottom: 0;
            }
        }

        .event-info {
            > div {
                margin-bottom: 1.5rem;

                &:last-of-type {
                    margin-bottom: 0;
                }
            }
        }
    }
}

.btn-event {
    @include hover-focus-active() {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
    }
}

.btn-event-day.btn-event-day {
    @include media-breakpoint-down(md) {
        padding: 6px !important;
    }
}

.card {
    &--appointments {
        height: 40vh;
        overflow-y: auto;

        .table-flex > div {
            min-height: 75px;
        }
    }
}

#my-calendar {
    .v-cal-header {
        margin-bottom: 2rem;

        &__actions {
            display: flex;
            align-items: center;
            justify-content: space-between;

            @include media-breakpoint-down(sm) {
                flex-direction: column;
            }
        }

        &__title {
            padding: 1rem 0;
        }
    }
}

@include media-breakpoint-down(sm) {
    #my-calendar {
        overflow: hidden;

        .v-cal-header__actions {
            display: flex;
            align-items: center;
            margin-bottom: 3rem;

            .actions-left {
                margin-bottom: 1rem;
            }
        }

        .v-cal-content {
            overflow-x: auto;

            &--week {
                .v-cal-weekdays {
                    max-height: 5rem;
                }
            }
        }
    }

    .v-cal-dialog {
        &-card {
            margin-top: 30px;
        }
    }
}
</style>
